import type { DeliveryMethodKind } from "@dransay/api/models";

export interface FormatDeliveryPriceLabelOptions {
	deliveryPrice?: number | null;
	deliveryMethodKind?: DeliveryMethodKind;
	totalPrice: number;
	deliveryMethodFreeFrom?: number | null;
	formatCurrency: (cents: number) => string;
	pendingPriceLabel: string;
}

/** Pickup has no cost in v1 aggregation; null means free, not unconfigured. */
export function resolveDeliveryPriceForDisplay(
	deliveryMethodKind: DeliveryMethodKind | undefined,
	deliveryPrice: number | null | undefined,
): number | null | undefined {
	if (
		deliveryMethodKind === "pickup" &&
		(deliveryPrice === null || deliveryPrice === undefined)
	) {
		return 0;
	}
	return deliveryPrice;
}

export function isDeliveryCostPending(
	deliveryMethodKind: DeliveryMethodKind | undefined,
	deliveryPrice: number | null | undefined,
): boolean {
	const resolved = resolveDeliveryPriceForDisplay(
		deliveryMethodKind,
		deliveryPrice,
	);
	return resolved === null || resolved === undefined;
}

export interface FormattedDeliveryPriceLabel {
	before: string | null;
	after: string;
}

export function hasConfiguredDeliveryCost(
	deliveryPrice: number | null | undefined,
): deliveryPrice is number {
	return deliveryPrice != null && deliveryPrice > 0;
}

export function canShowFreeShippingThreshold(
	deliveryPrice: number | null | undefined,
	deliveryMethodFreeFrom: number | null | undefined,
): deliveryMethodFreeFrom is number {
	return (
		hasConfiguredDeliveryCost(deliveryPrice) &&
		typeof deliveryMethodFreeFrom === "number"
	);
}

export function formatDeliveryPriceLabel({
	deliveryPrice,
	deliveryMethodKind,
	totalPrice,
	deliveryMethodFreeFrom,
	formatCurrency,
	pendingPriceLabel,
}: FormatDeliveryPriceLabelOptions): FormattedDeliveryPriceLabel {
	const resolvedDeliveryPrice = resolveDeliveryPriceForDisplay(
		deliveryMethodKind,
		deliveryPrice,
	);
	if (resolvedDeliveryPrice === null || resolvedDeliveryPrice === undefined) {
		return {
			before: null,
			after: pendingPriceLabel,
		};
	}
	if (
		canShowFreeShippingThreshold(
			resolvedDeliveryPrice,
			deliveryMethodFreeFrom,
		) &&
		totalPrice >= deliveryMethodFreeFrom
	) {
		return {
			before: formatCurrency(resolvedDeliveryPrice),
			after: formatCurrency(0),
		};
	}
	return {
		before: null,
		after: formatCurrency(resolvedDeliveryPrice),
	};
}
